import { useState, useRef, useEffect, useCallback } from "react";
import { FaBell } from "react-icons/fa";
import Token from "../../database/Token";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import env from "../../data/env";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import NotificationAudio from "./notification.mp3";
import axios from "axios";

dayjs.extend(relativeTime);

const TYPE_STYLES = {
  success: "bg-success-500",
  warning: "bg-warning-500",
  danger: "bg-danger-500",
  calendar: "bg-primary-500",
  lead: "bg-purple-500",
  reminder: "bg-orange-500",
  followup: "bg-cyan-500",
  info: "bg-gray-500",
};

export default function NotificationWidgets() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  const [isRinging, setIsRinging] = useState(false);

  const menuRef = useRef(null);
  const audioRef = useRef(null);
  const defaultFaviconRef = useRef("");
  const user = JSON.parse(localStorage.getItem("user") || "null");

  /*
  |--------------------------------------------------------------------------
  | BROWSER PERMISSION HANDLER (MUST BE TIED TO A CLICK)
  |--------------------------------------------------------------------------
  */
  const requestNativePermissions = async () => {
    if (!("Notification" in window)) return;

    if (Notification.permission === "default") {
      try {
        await Notification.requestPermission();
      } catch (err) {
        console.error("Failed to request notification permission:", err);
      }
    }
  };

  /*
  |--------------------------------------------------------------------------
  | PLAY SOUND
  |--------------------------------------------------------------------------
  */
  const playNotificationSound = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.volume = 1;
      audio.currentTime = 0;
      await audio.play();
    } catch (err) {
      console.log("Audio play blocked or failed:", err);
    }
  };

  useEffect(() => {
    const unlockAudio = async () => {
      const audio = audioRef.current;
      if (!audio) return;
      try {
        audio.volume = 0;
        await audio.play();
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 1;
      } catch (err) {}
    };

    window.addEventListener("pointerdown", unlockAudio, { once: true });
    window.addEventListener("keydown", unlockAudio, { once: true });

    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  useEffect(() => {
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon && !defaultFaviconRef.current) {
      defaultFaviconRef.current = favicon.href;
    }
  }, []);

  useEffect(() => {
    document.title =
      unreadCount > 0 ? `(${unreadCount}) ${env.APP_NAME}` : env.APP_NAME;
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) {
      favicon.href =
        unreadCount > 0
          ? "/favicon-notification.png"
          : defaultFaviconRef.current || "/favicon.ico";
    }
  }, [unreadCount]);

  /*
  |--------------------------------------------------------------------------
  | CORE NOTIFICATION HANDLER
  |--------------------------------------------------------------------------
  */
  const handleIncomingNotification = useCallback((notification) => {
    playNotificationSound();

    // 1. Trigger bell wiggle animation
    setIsRinging(true);
    setTimeout(() => setIsRinging(false), 3000);

    // 2. Fire the NATIVE OS Notification (Windows/macOS)
    if ("Notification" in window && Notification.permission === "granted") {
      const systemNotification = new Notification(
        notification.title || "New Alert",
        {
          body:
            notification.message ||
            "You have a new notification in the system.",
          icon: "/logo192.png", // Ensure this path points to a real image in your public folder
          requireInteraction: false, // Allows it to fade out naturally
          silent: true, // We are handling the audio manually via our mp3 file
        }
      );

      // Optional: If they click the native OS notification, focus the browser window
      systemNotification.onclick = () => {
        window.focus();
        systemNotification.close();
      };
    }

    // 3. Fire the In-App React Toast
    toast.custom(
      (t) => (
        <div
          className={`
        bg-white dark:bg-surface-darkCard shadow-2xl rounded-3xl p-5 border border-surface-border dark:border-surface-darkBorder w-[360px]
        transform transition-all duration-500 ease-out
        ${
          t.visible
            ? "translate-y-0 opacity-100 scale-100"
            : "-translate-y-10 opacity-0 scale-95"
        }
      `}
        >
          <div className="flex gap-4">
            <div
              className={`
            w-12 h-12 rounded-2xl text-white flex items-center justify-center shrink-0
            ${TYPE_STYLES[notification.type] || TYPE_STYLES.info}
            animate-pulse
          `}
            >
              <FaBell size={18} />
            </div>

            <div className="min-w-0">
              <div className="font-bold text-gray-800 dark:text-white text-md">
                {notification.title}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                {notification.message}
              </div>
            </div>
          </div>
        </div>
      ),
      {
        duration: 6000,
        position: "top-right",
      }
    );

    setNotifications((prev) => [notification, ...prev]);
    setUnreadCount((prev) => prev + 1);
  }, []);

  /*
  |--------------------------------------------------------------------------
  | KEYBOARD SHORTCUT (TESTING)
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === "q" || e.key === "Q")) {
        e.preventDefault();

        // If testing shortcut is used, ask for permission just in case
        requestNativePermissions();

        const types = Object.keys(TYPE_STYLES);
        const randomType = types[Math.floor(Math.random() * types.length)];

        const fakeNotification = {
          id: `fake-${Date.now()}`,
          title: "Test System Alert",
          message: "This is a fake payload generated by Ctrl+Shift+Q.",
          type: randomType,
          is_read: false,
          created_at: new Date().toISOString(),
          link: "#",
        };

        handleIncomingNotification(fakeNotification);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleIncomingNotification]);

  /*
  |--------------------------------------------------------------------------
  | REALTIME NOTIFICATIONS (PUSHER)
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    if (!user?.id) return;

    let echo;
    let channel;

    const initEcho = async () => {
      try {
        window.Pusher = Pusher;

        await axios.get(`${env.BACKEND_URL}/sanctum/csrf-cookie`, {
          withCredentials: true,
          withXSRFToken: true,
        });

        echo = new Echo({
          broadcaster: "pusher",
          key: env.PUSHER_APP_KEY,
          cluster: env.PUSHER_APP_CLUSTER,
          forceTLS: false,
          authorizer: (channel) => ({
            authorize: async (socketId, callback) => {
              try {
                const response = await Token.post(
                  `${env.BACKEND_URL}/broadcasting/auth`,
                  { socket_id: socketId, channel_name: channel.name },
                  { withCredentials: true, withXSRFToken: true }
                );
                callback(false, response.data);
              } catch (error) {
                console.error(error.response?.data);
                callback(true, error);
              }
            },
          }),
        });
        window.Echo = echo;

        channel = echo.private(`user.${user.id}`);

        channel.listen(".notification.created", (e) => {
          handleIncomingNotification(e?.notification || e);
        });
      } catch (err) {
        console.error("Echo initialization failed:", err);
      }
    };

    initEcho();

    return () => {
      if (channel) channel.stopListening(".notification.created");
      if (echo) echo.disconnect();
    };
  }, [user?.id, handleIncomingNotification]);

  /*
  |--------------------------------------------------------------------------
  | FETCH NOTIFICATIONS
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    fetchNotifications();
  }, [user?.id]);

  const fetchNotifications = async () => {
    try {
      const res = await Token.get("/notifications");
      const data = res.data?.data ?? res.data ?? [];
      setNotifications(data.map((n) => ({ ...n, isDeleting: false })));
      setUnreadCount(data.filter((n) => !n.is_read).length);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    }
  };

  const deleteNotification = async (id) => {
    setDeletingId(id);
    try {
      if (!String(id).startsWith("fake-")) {
        await Token.delete(`/notifications/${id}`);
      }

      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isDeleting: true } : n))
      );

      const target = notifications.find((n) => n.id === id);
      if (target && !target.is_read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        setDeletingId(null);
      }, 300);
    } catch (err) {
      console.error("Failed to delete notification", err);
      setDeletingId(null);
    }
  };

  const markAllRead = async () => {
    try {
      await Token.post("/notifications/mark-all-read");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all notifications as read", err);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <audio ref={audioRef} src={NotificationAudio} preload="auto" />

      {/* Modified: Added requestNativePermissions() to the onClick handler.
        The browser will now securely show the "Allow Notifications" prompt 
        the very first time the user clicks this bell. 
      */}
      <button
        onClick={() => {
          requestNativePermissions();
          setOpen(!open);
        }}
        className={`relative w-10 h-10 rounded-2xl transition-all duration-200 flex items-center justify-center border
          ${
            open
              ? "bg-primary-500 border-primary-500 text-white shadow-xl shadow-primary-500/25"
              : "bg-white dark:bg-surface-darkCard border-surface-border dark:border-surface-darkBorder text-gray-600 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-surface-darkMuted hover:text-primary-500"
          }
        `}
      >
        <FaBell
          size={14}
          className={
            isRinging ? "animate-[wiggle_1s_ease-in-out_infinite]" : ""
          }
        />

        {unreadCount > 0 && (
          <>
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-danger-500 animate-ping" />
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-danger-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold shadow-lg shadow-danger-500/30">
              {unreadCount}
            </span>
          </>
        )}
      </button>

      <div
        className={`absolute right-0 mt-3 w-[380px] bg-white dark:bg-surface-darkCard border border-surface-border dark:border-surface-darkBorder rounded-[28px] shadow-2xl overflow-hidden z-50 origin-top-right transition-all duration-300 ease-out
          ${
            open
              ? "scale-100 opacity-100 pointer-events-auto translate-y-0"
              : "scale-95 opacity-0 pointer-events-none -translate-y-2"
          }
        `}
      >
        <div className="px-6 py-5 border-b border-surface-border dark:border-surface-darkBorder bg-gradient-to-r from-primary-50 to-white dark:from-surface-darkMuted dark:to-surface-darkCard">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-white">
                Notifications
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                You have {unreadCount} unread notifications
              </p>
            </div>

            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="h-9 px-4 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>
        </div>

        <ul className="max-h-[420px] overflow-y-auto scroll-smooth">
          {notifications.length === 0 && (
            <li className="flex flex-col items-center justify-center py-14 px-6 text-center">
              <div className="w-16 h-16 rounded-3xl bg-primary-50 dark:bg-surface-darkMuted flex items-center justify-center text-primary-500 mb-4 transition-transform hover:scale-110">
                <FaBell size={20} />
              </div>
              <h4 className="font-semibold text-gray-700 dark:text-white">
                No Notifications
              </h4>
            </li>
          )}

          {notifications.map((n) => (
            <li
              key={n.id}
              className={`group relative px-6 transition-all duration-300 ease-in-out border-b border-surface-border dark:border-surface-darkBorder overflow-hidden
                ${
                  n.isDeleting
                    ? "max-h-0 opacity-0 py-0 border-none scale-95"
                    : "max-h-[200px] py-5"
                }
                ${
                  !n.is_read
                    ? "bg-primary-50/60 dark:bg-primary-900/10"
                    : "bg-white dark:bg-surface-darkCard hover:bg-gray-50 dark:hover:bg-surface-darkMuted"
                }
              `}
            >
              {!n.is_read && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-500 rounded-r-full" />
              )}

              <div className="absolute top-4 right-4 z-10">
                {deletingId === n.id ? (
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent dark:border-gray-500 dark:border-t-transparent rounded-full animate-spin" />
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      deleteNotification(n.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 transition-all duration-200 hover:text-red-600 hover:rotate-90 text-gray-400 dark:hover:text-red-500"
                  >
                    ✕
                  </button>
                )}
              </div>

              <Link to={n.link || "#"} className="block">
                <div className="flex gap-4">
                  <div
                    className={`w-11 h-11 rounded-2xl shrink-0 text-white flex items-center justify-center shadow-md transition-transform group-hover:scale-105 ${
                      TYPE_STYLES[n.type] || TYPE_STYLES.info
                    }`}
                  >
                    <FaBell size={14} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-800 dark:text-white pr-4 truncate">
                      {n.title}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                      {n.message}
                    </p>
                    <div className="mt-3 text-[11px] text-gray-400 font-medium">
                      {dayjs(n.created_at).fromNow()}
                    </div>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
